import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown, Plus } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// Pages whose hero is dark/coloured — header uses white text when transparent
const DARK_HERO_PAGES = ['/pharmacy', '/services', '/franchise', '/investors', '/careers', '/contact'];

// Pages whose hero is light — header uses dark text even when transparent
const LIGHT_HERO_PAGES = ['/', '/about'];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();

  // Reset scroll state on route change
  useEffect(() => {
    setIsScrolled(window.scrollY > 40);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setServicesOpen(false);
  }, [location]);

  const navLinks = [
    { name: t.nav.home, path: '/' },
    { name: t.nav.about, path: '/about' },
    {
      name: t.nav.business,
      path: '#',
      dropdown: [
        { name: t.nav.services, path: '/services' },
        { name: t.nav.pharmacy, path: '/pharmacy' },
        { name: t.nav.franchise, path: '/franchise' },
      ],
    },
    { name: t.nav.investors, path: '/investors' },
    { name: t.nav.careers, path: '/careers' },
    { name: t.nav.contact, path: '/contact' },
  ];

  const isActive = (path: string) => location.pathname === path;

  const hasDarkHero = DARK_HERO_PAGES.includes(location.pathname);
  const hasLightHero = LIGHT_HERO_PAGES.includes(location.pathname);

  // Transparent mode: on any hero page when not scrolled and no mobile menu
  const transparent = (hasDarkHero || hasLightHero) && !isScrolled && !mobileMenuOpen;

  // On light hero (home): use dark text even when transparent
  // On dark hero: use white text when transparent
  // When solid (scrolled): always dark text
  const useDarkText = !transparent || hasLightHero;
  const textMuted = useDarkText ? 'text-gray-500' : 'text-white/75';
  const hoverText = useDarkText ? 'hover:text-primary-600' : 'hover:text-white';
  const activeText = useDarkText ? 'text-primary-600' : 'text-secondary-300';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        transparent
          ? hasLightHero
            ? 'bg-transparent py-5 border-b border-gray-200/60'
            : 'bg-transparent py-5'
          : 'bg-white/95 backdrop-blur-sm shadow-sm border-b border-gray-100 py-3'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Mobile: flex row logo-left + toggle-right | Desktop: 3-col grid logo | nav | actions */}
        <div className="flex items-center justify-between lg:grid lg:grid-cols-[200px_1fr_200px] gap-4">

          {/* ── Brand Logo (left) ───────────────────────────────── */}
          <Link to="/" className="flex items-center group flex-shrink-0 justify-start">
            <div className="flex flex-col leading-none">
              <span className="font-heading font-black text-2xl tracking-tight select-none inline-flex items-center"
                style={{
                  background: 'linear-gradient(90deg, #e51c1c, #f97316)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                APlus
                {/* Plus icon with gradient via SVG filter trick — use a wrapper with gradient bg and mask */}
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '20px',
                    height: '20px',
                    minWidth: '20px',
                    minHeight: '20px',
                    backgroundColor: '#e5e7eb',
                    borderRadius: '3px',
                    marginLeft: '3px',
                    flexShrink: 0,
                    WebkitTextFillColor: 'initial',
                  }}
                >
                  <Plus style={{ width: '18px', height: '18px', color: '#e51c1c', strokeWidth: 3.5 }} />
                </span>
              </span>
              {/* HEALTHCARE stretched to fill full width of "APlus +" */}
              <span className={`text-[10px] font-semibold uppercase mt-0.5 transition-colors duration-300 w-full tracking-widest ${
                transparent && !hasLightHero ? 'text-white/50' : 'text-gray-400'
              }`} style={{ letterSpacing: '0.3em', display: 'block', textAlign: 'justify', textAlignLast: 'justify' }}>Healthcare</span>
            </div>
          </Link>

          {/* ── Desktop Navigation (center) ─────────────────────── */}
          <nav className="hidden lg:flex items-center justify-center gap-5">
            {navLinks.map((link) =>
              link.dropdown ? (
                <div key={link.name} className="relative group">
                  <button className={`flex items-center gap-1 py-2 text-sm font-semibold transition-colors duration-200 ${textMuted} ${hoverText}`}>
                    {link.name}
                    <ChevronDown className="w-3.5 h-3.5 transition-transform duration-200 group-hover:rotate-180" />
                  </button>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-52 bg-white rounded-xl shadow-2xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 translate-y-1 group-hover:translate-y-0 z-50">
                    {link.dropdown.map((item) => (
                      <Link
                        key={item.name}
                        to={item.path}
                        className="block px-4 py-3 text-gray-700 hover:text-primary-600 hover:bg-primary-50 transition-colors first:rounded-t-xl last:rounded-b-xl font-medium text-sm"
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`py-2 text-sm font-semibold transition-colors duration-200 whitespace-nowrap ${
                    isActive(link.path) ? activeText : `${textMuted} ${hoverText}`
                  }`}
                >
                  {link.name}
                </Link>
              )
            )}
          </nav>

          {/* ── Right: Phone number (desktop) + Mobile Toggle ───── */}
          <div className="flex items-center justify-end gap-3">
            {/* Desktop: phone number + catchy sub-text */}
            <a
              href={`tel:${t.nav.phone.replace(/\s/g, '')}`}
              className={`hidden lg:flex flex-col items-end transition-colors duration-200 group`}
            >
              <span className={`flex items-center gap-1.5 text-sm font-bold transition-colors duration-200 ${textMuted} group-hover:text-primary-600`}>
                <Phone className="w-3.5 h-3.5" />
                {t.nav.phone}
              </span>
              <span
                className={`text-[10px] font-semibold tracking-wide ${
                  useDarkText ? 'text-primary-600' : 'text-white/70'
                }`}
              >
                Let's build something together
              </span>
            </a>

            {/* Mobile Toggle */}
            <button
              className={`lg:hidden p-2 rounded-lg transition-colors ${
                transparent && !hasLightHero
                  ? 'text-white hover:bg-white/15'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* ── Mobile Menu ────────────────────────────────────────── */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-xl">
          <div className="max-w-7xl mx-auto px-4 py-4 space-y-1">
            {navLinks.map((link) =>
              link.dropdown ? (
                <div key={link.name}>
                  <button
                    onClick={() => setServicesOpen(!servicesOpen)}
                    className="w-full flex items-center justify-between px-4 py-3 text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg font-semibold text-sm transition-colors"
                  >
                    {link.name}
                    <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {servicesOpen && (
                    <div className="ml-4 border-l-2 border-primary-100 pl-4 space-y-1 mt-1">
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.name}
                          to={item.path}
                          className="block px-4 py-2.5 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg text-sm font-medium transition-colors"
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`block px-4 py-3 rounded-lg font-semibold text-sm transition-colors ${
                    isActive(link.path)
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-gray-700 hover:text-primary-600 hover:bg-primary-50'
                  }`}
                >
                  {link.name}
                </Link>
              )
            )}

            <div className="pt-4 border-t border-gray-100 space-y-3">
              <p className="text-center text-xs font-semibold tracking-wide text-primary-600">
                Let's build something together
              </p>
              <a
                href={`tel:${t.nav.phone.replace(/\s/g, '')}`}
                className="flex items-center justify-center gap-2 text-primary-600 font-semibold py-3 px-6 rounded-lg text-sm border border-primary-200 bg-primary-50 hover:bg-primary-100 transition-all w-full"
              >
                <Phone className="w-4 h-4" />
                {t.nav.phone}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
